import axios from "axios";
import useLogin from "hooks/useLogin";
import { URL_LIST } from "constants/urlApis";
import { endpointError } from "utils/codeErrorsTimeout";
import buildQueryParams from "utils/buildQueryParams";

export default function useList() {
  const { jwt } = useLogin();
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };

  /**
   * Creates a new list with items.
   * This function sends a POST request to create a new list along with its items.
   * @param {Object} list - The list data to be created. This should include the list details and items.
   * @param {Object} list.items - The items to be included in the list.
   * @returns {Promise<Object>} A promise that resolves to the server's response after creating the list.
   * @throws {Error} Throws an error if the request fails or if the API returns an error response.
   */
  const createListWithItem = async (list) => {
    const response = await axios.post(`${URL_LIST}/form/lists/`, list, {
      headers,
      endpointError: endpointError.list.createListWithItem,
    });
    return response;
  };

  /**
   * Adds an item to a specified list.
   * This function sends a POST request to add a new item to an existing list identified by `listId`.
   * @param {Object} item - The item data to be added to the list.
   * @param {string} listId - The ID of the list to which the item will be added.
   * @returns {Promise<Object>} A promise that resolves to the server's response after adding the item.
   * @throws {Error} Throws an error if the request fails or if the API returns an error response.
   */
  const createItemsInList = async (item, listId) => {
    const response = await axios.post(`${URL_LIST}/form/lists/${listId}/items/`, item, {
      headers,
      endpointError: endpointError.list.createItemsInList,
    });
    return response;
  };

  /**
   * Retrieves a list of forms with optional filters.
   * This function fetches a list of forms from the server, optionally filtered by status or name.
   * If status and search are provided, both filters are applied to the results.
   * @param {number} page - The page number to retrieve (for pagination).
   * @param {number} per_page - The number of items per page.
   * @param {string} [search] - The search term for filtering by form name. If not provided, the search term is ignored.
   * @param {string} [status] - The status to filter the forms by. If not provided, the filter by status is ignored.
   * @returns {Promise<Object>} A promise that resolves to the list of forms retrieved from the server.
   * @throws {Error} Throws an error if the request fails or if the API returns an error response.
   */
  const getLists = async (page, per_page, search, status) => {
    const queryParams = buildQueryParams({
      name: search,
      status,
      page,
      per_page,
    });
    const response = await axios.get(`${URL_LIST}/form/lists/?${queryParams}`, {
      headers,
      endpointError: endpointError.list.getLists,
    });

    return response.data;
  };

  /**
   * Retrieves a specific list by its ID.
   * This function fetches details of a specific list from the server using its unique identifier.
   * @param {string} listId - The unique identifier of the list to retrieve.
   * @returns {Promise<Object>} A promise that resolves to the details of the list retrieved from the server.
   * @throws {Error} Throws an error if the request fails or if the API returns an error response.
   */
  const getListsById = async (listId) => {
    const response = await axios.get(`${URL_LIST}/form/lists/${listId}/`, {
      headers,
      endpointError: endpointError.list.getListsById,
    });
    return response.data;
  };

  /**
   * Updates the details of a specific list.
   * This function sends a PATCH request to update either the name or the status of a list using its unique identifier.
   * @param {string} listId - The unique identifier of the list to update.
   * @param {string} [listName] - The new name for the list. If not provided, the status will be updated.
   * @param {string} [listStatus] - The new status for the list. If not provided, the name will be updated.
   * @returns {Promise<Object>} A promise that resolves to the response from the server after the update operation.
   * @throws {Error} Throws an error if the request fails or if the API returns an error response.
   */
  const updateList = async (listId, listName, listStatus) => {
    const response = await axios.patch(
      `${URL_LIST}/form/lists/${listId}/`,
      listName || listStatus,
      {
        headers,
        endpointError: endpointError.list.updateList,
      }
    );
    return response;
  };

  /**
   * Retrieves items from a specific list.
   * This function sends a GET request to fetch items from a list identified by its unique identifier.
   * It supports pagination and allows filtering items by name.
   * @param {string} listId - The unique identifier of the list from which to retrieve items.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page.
   * @param {string} [search] - An optional search query to filter items by name.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the list items.
   * @throws {Error} Throws an error if the request fails or if the API returns an error response.
   */
  const getItems = async (listId, page, per_page, search, parentId, operator) => {
    const params = buildQueryParams({ page, per_page, name: search, parent_id: parentId, operator });
    const response = await axios.get(
      `${URL_LIST}/form/lists/${listId}/items/?${params}`,
      {
        headers,
        endpointError: endpointError.list.getItems,
      }
    );
    return response.data;
  };

  /**
   * Retrieves a specific item from a list by its unique identifier.
   * This function sends a GET request to fetch a single item identified by its unique identifier from a specific list.
   * @param {string} listId - The unique identifier of the list containing the item.
   * @param {string} itemId - The unique identifier of the item to retrieve.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the item details.
   * @throws {Error} Throws an error if the request fails or if the API returns an error response.
   */
  const getItemsById = async (listId, itemId) => {
    const response = await axios.get(`${URL_LIST}/form/lists/${listId}/items/${itemId}/`, {
      headers,
      endpointError: endpointError.list.getItemsById,
    });
    return response.data;
  };

  /**
   * Updates the name of a specific item within a list.
   * This function sends a PATCH request to update the name of an item identified by its unique identifier in a specified list.
   * @param {string} listId - The unique identifier of the list containing the item.
   * @param {string} itemId - The unique identifier of the item to update.
   * @param {string} itemName - The new name to assign to the item.
   * @returns {Promise<Object>} A promise that resolves to the response data from the update request.
   * @throws {Error} Throws an error if the request fails or if the API returns an error response.
   */
  const updateItem = async (listId, itemId, itemName) => {
    const response = await axios.patch(
      `${URL_LIST}/form/lists/${listId}/items/${itemId}/`,
      itemName,
      {
        headers,
        endpointError: endpointError.list.updateItem,
      }
    );
    return response;
  };

  return {
    getLists,
    createListWithItem,
    createItemsInList,
    updateList,
    getListsById,
    getItems,
    getItemsById,
    updateItem,
  };
}
